import { AfterViewInit, ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, OnInit, signal, ViewChild, WritableSignal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatDrawer, MatSidenavModule } from '@angular/material/sidenav';
import { ActivatedRoute } from '@angular/router';
import { CountableTimeZonePipe } from 'countable@helpers';
import { ClassNamesScheduleModel, EngagementScheduleModel, InputOutputEmitter, TitleIconScheduleModel } from 'countable@model';
import { ToastrService } from 'ngx-toastr';
import { Observable } from 'rxjs';
import { CanComponentDeactivate } from '../../../../can-deactivate.guard';
import { ScheduleService } from '../../../../services/schedule.service';
import { PreviewScheduleComponent } from '../../../remote-shared/schedules/preview-schedule/preview-schedule.component';
import { ViewScheduleComponent } from './view-schedule/view-schedule.component';
import {finalize} from "rxjs/operators";

@Component({
  selector: 'app-working-schedules',
  imports: [ViewScheduleComponent, MatButtonModule, MatSidenavModule, PreviewScheduleComponent, CountableTimeZonePipe],
  templateUrl: './working-schedules.component.html',
  styleUrl: './working-schedules.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class WorkingSchedulesComponent implements OnInit , AfterViewInit , CanComponentDeactivate {
  @ViewChild('drawer') drawer: MatDrawer;
  protected readonly InputOutputEmitter= InputOutputEmitter;
  protected readonly classNamesScheduleModel: typeof ClassNamesScheduleModel= ClassNamesScheduleModel;
  public viewId: WritableSignal<string> = signal<string>(null);
  public engagementScheduleDetails: WritableSignal<EngagementScheduleModel> = signal<EngagementScheduleModel>(new EngagementScheduleModel());
  public scheduleName: WritableSignal<string> = signal<string>('');
  public expandOrCollapse: WritableSignal<TitleIconScheduleModel> = signal<TitleIconScheduleModel>(new TitleIconScheduleModel());
  public refreshOrExpandEvent: WritableSignal<InputOutputEmitter> = signal<InputOutputEmitter>(null);
  public isSyncCall: WritableSignal<boolean> = signal<boolean>(false);
  private destroyRef = inject(DestroyRef);

  isGroup = computed(()=>{
    return !!(this.expandOrCollapse().title && this.expandOrCollapse().icon);
  })

  constructor(private activatedRoute: ActivatedRoute, private scheduleService: ScheduleService,
              private countableTimeZonePipe: CountableTimeZonePipe, public toasterService: ToastrService) {
    effect(() => {
      this.engagementScheduleDetails.set(this.scheduleService.engagementScheduleSignal());
      if (this.engagementScheduleDetails().schedulesData?.schedules?.length && this.engagementScheduleDetails().schedulesData?.schedules.filter((s: any) => s.uuid === this.viewId()).length) {
        const selectedSchedule = this.engagementScheduleDetails().schedulesData?.schedules.filter((s: any) => s.uuid === this.viewId())[0];
        this.scheduleName.set(`Schedule ${selectedSchedule.sortOrder} - ${selectedSchedule.nameChange ? selectedSchedule.nameChange : selectedSchedule.name }`);
      }
    });
  }

  ngOnInit(): void {
    this.activatedRoute.queryParams.subscribe(param => {
      if(param?.NoteId){
        this.viewId.set(param?.NoteId ? param?.NoteId : null);
      }
    });

    this.scheduleService.syncCallSpinner$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(value=>{
      this.isSyncCall.set(value);
    });
  }

  ngAfterViewInit() {
    this.drawer.open();
  }

  protected expandOrCollapsed(event: any){
    if(!event){
      this.expandOrCollapse.set(new TitleIconScheduleModel());
      return;
    }

    const isExpand = event === this.InputOutputEmitter.EXPAND;
    this.expandOrCollapse.update((config) => ({ ...config, title: isExpand ? this.classNamesScheduleModel.TITLE_COLLAPSED : this.classNamesScheduleModel.TITLE_EXPAND ,
    icon: isExpand ? this.classNamesScheduleModel.ICON_COLLAPSE : this.classNamesScheduleModel.ICON_EXPAND, isExpand: isExpand}));

  }

  protected onExpandOrRefresh(value: InputOutputEmitter){
    const isExpand = value === this.InputOutputEmitter.EXPAND && this.expandOrCollapse().isExpand ? this.InputOutputEmitter.COLLAPSED :   this.InputOutputEmitter.EXPAND;
    if((value === this.InputOutputEmitter.REFRESH)){
      const selectedSchedule = this.engagementScheduleDetails().schedulesData?.schedules.filter((s: any) => s.uuid === this.viewId())[0];
      selectedSchedule?.isManuallyUpdate ? this.scheduleService.setSync(this.viewId()) : this.schedulesSync();
    }else{
      this.refreshOrExpandEvent.set(isExpand);
      this.expandOrCollapsed(isExpand);
    }
  }

  protected schedulesSync() {
    const engagementData = JSON.parse(localStorage.getItem('selectedEngagementObj'));
    this.scheduleService.updateSpinner(true);
    this.scheduleService.updateSyncCallSpinner(true);
    this.scheduleService.commonPutMethod('/sync', {
      'engagementId': engagementData?.engagementsid,
      'acctYear': +this.countableTimeZonePipe.transform(engagementData?.yearEnd, 'yyyy', 'UTC'),
      'uuid': this.viewId(),
    }).pipe(finalize(() => this.scheduleService.updateSpinner(false))).subscribe({
      next: response =>{
        if(response.status === 200){
          this.scheduleService.setSync(this.viewId());
        }
      },
      error: e=>{
        this.toasterService.error(e.error);
        this.scheduleService.updateSyncCallSpinner(false);
      }
    });
  }

  canDeactivate(): boolean | Observable<boolean> | Promise<boolean> {
    return this.scheduleService.saveChanges.getValue();
  }
}
